import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2, ChevronDown, CalendarDays, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { listResource, createResource, updateResource, deleteResource } from "@/lib/api";
import { PageHeader, StatusBadge, displayLabel, fmtDate } from "@/components/common";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ymd = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";
const parseYmd = (s) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s || "")) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const DateRangeField = ({ fl, values, setValues }) => {
  const s = values[fl.startKey];
  const e = values[fl.endKey];
  const label = s ? (e && e !== s ? `${fmtDate(s)} – ${fmtDate(e)}` : fmtDate(s)) : "Pick dates";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={`field-${fl.key}`}
          className="mt-1 w-full justify-start rounded-none border-2 font-normal">
          <CalendarDays className="w-4 h-4 mr-2 text-gray-500" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 rounded-none border-2 border-gray-900">
        <Calendar
          mode="range"
          numberOfMonths={1}
          selected={{ from: parseYmd(s), to: parseYmd(e) }}
          onSelect={(range) => setValues({ ...values, [fl.startKey]: ymd(range?.from), [fl.endKey]: ymd(range?.to) })}
        />
      </PopoverContent>
    </Popover>
  );
};

const InlineStatus = ({ badge, options, onSelect, testId }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid={testId} className="inline-flex items-center gap-1 group focus:outline-none">
        {badge}
        <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-900 transition-colors" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="rounded-none border-2 border-gray-900">
      {options.map((o) => (
        <DropdownMenuItem key={o} data-testid={`status-option-${o}`} onClick={() => onSelect(o)} className="cursor-pointer focus:bg-gray-100">
          <StatusBadge status={o} />
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const emptyForm = (fields) => {
  const f = {};
  fields.forEach((fl) => { f[fl.key] = fl.default ?? (fl.type === "checkbox" ? false : ""); });
  return f;
};

const toFormValues = (fields, row) => {
  const f = {};
  fields.forEach((fl) => {
    let v = row[fl.key];
    if (fl.type === "list" && Array.isArray(v)) v = v.join("\n");
    f[fl.key] = v ?? (fl.default ?? "");
  });
  return f;
};

const EntityForm = ({ fields, values, setValues }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {fields.map((fl) => (
      fl.type === "hidden" ? null : (
      <div key={fl.key} className={fl.full ? "sm:col-span-2" : ""}>
        <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">{fl.label}{fl.required && <span className="text-[#D50000]"> *</span>}</Label>
        {fl.type === "daterange" ? (
          <DateRangeField fl={fl} values={values} setValues={setValues} />
        ) : fl.type === "select" ? (
          <Select value={values[fl.key]} onValueChange={(v) => setValues({ ...values, [fl.key]: v })}>
            <SelectTrigger data-testid={`field-${fl.key}`} className={`mt-1 rounded-none border-2 ${fl.inputClass || ""}`}>
              <SelectValue placeholder={`Select ${fl.label}`} />
            </SelectTrigger>
            <SelectContent>
              {fl.options.map((o) => (
                <SelectItem key={o} value={o} data-testid={`option-${fl.key}-${o}`}>
                  {displayLabel(o)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : fl.type === "checkbox" ? (
          <label className="mt-2 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid={`field-${fl.key}`} checked={!!values[fl.key]}
              onChange={(e) => setValues({ ...values, [fl.key]: e.target.checked })}
              className="w-5 h-5 accent-gray-900" />
            <span className="text-sm text-gray-700">{fl.checkboxLabel || "Yes"}</span>
          </label>
        ) : fl.type === "textarea" || fl.type === "list" ? (
          <Textarea
            data-testid={`field-${fl.key}`}
            className="mt-1 rounded-none border-2"
            value={values[fl.key]}
            placeholder={fl.placeholder}
            onChange={(e) => setValues({ ...values, [fl.key]: e.target.value })}
          />
        ) : (
          <Input
            data-testid={`field-${fl.key}`}
            type={fl.type === "number" ? "number" : fl.type === "date" ? "date" : fl.type === "tel" ? "tel" : "text"}
            className="mt-1 rounded-none border-2"
            value={values[fl.key]}
            placeholder={fl.placeholder}
            maxLength={fl.maxLength}
            onChange={(e) => setValues({ ...values, [fl.key]: e.target.value })}
          />
        )}
      </div>
      )
    ))}
  </div>
);

export default function CrudSection({ title, subtitle, path, fields, columns, testPrefix, extraHeader, extraFooter, addLabel, editLabel, category, filter, hideAdd, validate, readOnly, noDelete, searchable }) {
  const qc = useQueryClient();
  const canEdit = !readOnly;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [values, setValues] = useState(emptyForm(fields));

  const { data: allData = [], isLoading } = useQuery({
    queryKey: [path],
    queryFn: () => listResource(path),
    refetchInterval: 5000,
  });
  let data = category ? allData.filter((d) => d.category === category) : allData;
  if (filter) data = data.filter(filter);
  if (searchable && q.trim()) {
    const needle = q.trim().toLowerCase();
    data = data.filter((d) =>
      Object.values(d).some((v) => v != null && typeof v !== "object" && String(v).toLowerCase().includes(needle))
    );
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [path] });
    qc.invalidateQueries({ queryKey: ["summary"] });
  };

  const serializeValues = (v) => {
    const out = { ...v };
    fields.forEach((fl) => {
      if (fl.type === "number") out[fl.key] = Number(out[fl.key]) || 0;
      if (fl.type === "list") out[fl.key] = (out[fl.key] || "").split("\n").map((s) => s.trim()).filter(Boolean);
    });
    return out;
  };

  const createMut = useMutation({
    mutationFn: (v) => createResource(path, { ...serializeValues(v), ...(category ? { category } : {}) }),
    onSuccess: () => { invalidate(); toast.success("Added"); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to add"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, v }) => updateResource(path, id, serializeValues(v)),
    onSuccess: () => { invalidate(); toast.success("Updated"); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to update"),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => deleteResource(path, id),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
    onError: () => toast.error("Failed to delete"),
  });
  const quickUpdateMut = useMutation({
    mutationFn: ({ id, patch }) => updateResource(path, id, patch),
    onSuccess: () => { invalidate(); toast.success("Status updated"); },
    onError: () => toast.error("Couldn't update status"),
  });

  const openAdd = () => { setEditing(null); setValues(emptyForm(fields)); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setValues(toFormValues(fields, row)); setOpen(true); };
  const submit = () => {
    const missing = fields.filter((f) => f.required && !String(values[f.key] ?? "").trim());
    if (missing.length) {
      toast.error(`Please fill in: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    if (validate) {
      const err = validate(values);
      if (err) { toast.error(err); return; }
    }
    if (editing) updateMut.mutate({ id: editing.id, v: values });
    else createMut.mutate(values);
  };
  const saving = createMut.isPending || updateMut.isPending;

  const renderCell = (row, c) => {
    if (c.editable && canEdit) {
      return (
        <InlineStatus
          testId={`${testPrefix}-status-quick`}
          badge={c.render ? c.render(row) : row[c.key]}
          options={c.options}
          onSelect={(val) => quickUpdateMut.mutate({ id: row.id, patch: { [c.key]: val } })}
        />
      );
    }
    return c.render ? c.render(row) : (row[c.key] || "—");
  };

  const rowActions = (row) => (
    <>
      <Button data-testid={`${testPrefix}-edit-btn`} size="icon" variant="ghost" className="rounded-none hover:bg-gray-200" onClick={() => openEdit(row)}>
        <Pencil className="w-4 h-4" />
      </Button>
      {!noDelete && (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button data-testid={`${testPrefix}-delete-btn`} size="icon" variant="ghost" className="rounded-none hover:bg-red-100 text-[#D50000]">
            <Trash2 className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="rounded-none border-2 border-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head uppercase">Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-2">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testPrefix}-confirm-delete-btn`} className="rounded-none bg-[#D50000] hover:bg-red-700" onClick={() => deleteMut.mutate(row.id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      )}
    </>
  );

  return (
    <div data-testid={`${testPrefix}-section`}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          canEdit && !hideAdd ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={`${testPrefix}-add-btn`} onClick={openAdd}
                className="rounded-none bg-gray-900 hover:bg-gray-700 text-white font-bold uppercase tracking-wide">
                <Plus className="w-4 h-4 mr-2" /> Add
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-2 border-gray-900 max-w-2xl max-h-[90vh] flex flex-col p-0">
              <DialogHeader className="shrink-0 p-6 pb-2">
                <DialogTitle className="font-head text-2xl font-bold uppercase tracking-tight">
                  {editing
                    ? (editLabel || `Edit ${title.replace(/s$/, "")}`)
                    : (addLabel || `New ${title.replace(/s$/, "")}`)}
                </DialogTitle>
                <DialogDescription>Fill in the details below and save.</DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 py-2">
                <EntityForm fields={fields} values={values} setValues={setValues} />
              </div>
              <DialogFooter className="shrink-0 p-6 pt-4 border-t border-gray-100 bg-white">
                <Button variant="outline" className="rounded-none border-2" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid={`${testPrefix}-save-btn`} onClick={submit} disabled={saving}
                  className="rounded-none bg-gray-900 hover:bg-gray-700 text-white font-bold uppercase">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          ) : null
        }
      />

      {extraHeader}

      {searchable && (
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            data-testid={`${testPrefix}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="w-full rounded-none border-2 border-gray-900 pl-9 pr-3 py-2 text-sm focus:outline-none"
          />
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block bg-white border-2 border-gray-200 overflow-x-auto">
        <table className="w-full border-collapse" data-testid={`${testPrefix}-table`}>
          <thead>
            <tr className="border-b-4 border-gray-900">
              {columns.map((c) => (
                <th key={c.key} className={`text-xs uppercase tracking-widest font-bold text-gray-500 px-5 py-4 ${c.align === "right" ? "text-right" : "text-left"}`}>
                  {c.label}
                </th>
              ))}
              {canEdit && <th className="px-5 py-4 text-right text-xs uppercase tracking-widest font-bold text-gray-500">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center text-gray-400">Loading…</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center text-gray-400">No records yet.</td></tr>
            ) : (
              data.map((row, i) => (
                <tr key={row.id} data-testid={`${testPrefix}-row`} className={`${i % 2 ? "bg-[#F9FAFB]" : "bg-white"} hover:bg-gray-100 transition-colors`}>
                  {columns.map((c) => (
                    <td key={c.key} className={`px-5 py-4 align-top text-sm lg:text-base ${c.align === "right" ? "text-right font-mono-data" : ""} ${c.mono ? "font-mono-data" : ""}`}>
                      {renderCell(row, c)}
                    </td>
                  ))}
                  {canEdit && (
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    {rowActions(row)}
                  </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid={`${testPrefix}-cards`}>
        {isLoading ? (
          <div className="bg-white border-2 border-gray-200 p-6 text-center text-gray-400">Loading…</div>
        ) : data.length === 0 ? (
          <div className="bg-white border-2 border-gray-200 p-6 text-center text-gray-400">No records yet.</div>
        ) : (
          data.map((row) => (
            <div key={row.id} data-testid={`${testPrefix}-card`} className="bg-white border-2 border-gray-200 p-4">
              {columns.map((c) => (
                <div key={c.key} className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
                  <span className="text-[11px] uppercase tracking-widest font-bold text-gray-500 pt-0.5 shrink-0">{c.label}</span>
                  <span className="text-right text-sm">{renderCell(row, c)}</span>
                </div>
              ))}
              {canEdit && <div className="flex gap-2 justify-end pt-3">{rowActions(row)}</div>}
            </div>
          ))
        )}
      </div>

      {extraFooter}
    </div>
  );
}
